// ArticleSection.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
    ArticleImage,
    ArticleTitle,
    ArticleDescription,
    ArticlesContainer,
    ScrollButton,
    ArticleItem,
    HeaderContainer,
    Title,
    FullBleed,
} from './articles.styles';
import MaterialIcon from '../../../../../../components/material-icon';
import { colorPalette } from '../../../../../../utils/theme';
import { ARTICLES } from '../../../../../../config/articleConfig';
import { Capacitor } from '@capacitor/core';
import useCustomNavigate from '../../../../../../hooks/useCustomNavigate';
import { useTranslation } from 'react-i18next';

const Articles: React.FC = () => {
    const navigate = useCustomNavigate();
    const { t } = useTranslation();

    // References
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // States
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const updateScrollButtons = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
        }
    };

    useEffect(() => {
        updateScrollButtons();
        const handleResize = () => updateScrollButtons();
        window.addEventListener('resize', handleResize);

        if (scrollContainerRef.current) {
            scrollContainerRef.current.addEventListener('scroll', updateScrollButtons);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (scrollContainerRef.current) {
                scrollContainerRef.current.removeEventListener('scroll', updateScrollButtons);
            }
        };
    }, []);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -300 : 300,
                behavior: 'smooth',
            });
            updateScrollButtons();
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0));
        setScrollLeft(scrollContainerRef.current?.scrollLeft || 0);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0);
        const walk = (x - startX) * 1.5;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollLeft - walk;
        }
    };

    const handleMouseUpOrLeave = () => {
        setIsDragging(false);
    };

    const handleOpenInNewTab = async (articleId: number) => {
        const url = `/articles/${articleId}`;

        if (Capacitor.isNativePlatform()) {
            // Native app – use internal navigation instead of window.open
            navigate(url);
        } else {
            // Web/PWA – open in new tab
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <ArticlesContainer>
            <HeaderContainer>
                <Title>{t("articles.articles_title")}</Title>
            </HeaderContainer>
            <FullBleed>
                <div className="scroll-wrapper">
                    {canScrollLeft && (
                        <ScrollButton className="scroll-left d-none d-md-inline" onClick={() => scroll('left')}>
                            <MaterialIcon icon="arrow_back" size={16} color={colorPalette.textMain} />
                        </ScrollButton>
                    )}
                    <div
                        className="updates-container"
                        ref={scrollContainerRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUpOrLeave}
                        onMouseLeave={handleMouseUpOrLeave}
                    >
                        {ARTICLES.map((article) => (
                            <ArticleItem key={article.id} onClick={() => handleOpenInNewTab(article.id)}>
                                <ArticleImage src={article.image} alt={String(t(article.titleKey as any))} draggable={false} />
                                <ArticleTitle>{t(article.titleKey as any)}</ArticleTitle>
                                <ArticleDescription>{t(article.descriptionKey as any)}</ArticleDescription>
                            </ArticleItem>
                        ))}
                    </div>
                    {canScrollRight && (
                        <ScrollButton className="scroll-right d-none d-md-inline" onClick={() => scroll('right')}>
                            <MaterialIcon icon="arrow_forward" size={16} color={colorPalette.textMain} />
                        </ScrollButton>
                    )}
                </div>
            </FullBleed>
        </ArticlesContainer>
    );
};

export default Articles;
